import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { APP_STORE_ADRESI } from '../../utils/whatsapp';
import './GuncellemeUyarisi.css';

/*
  ZORUNLU GÜNCELLEME UYARISI
  --------------------------
  NEDEN VAR: uygulama canlı siteyi yüklediği için arayüz güncellemeleri
  herkese anında gidiyor. Ama widget'lar, telefon bildirimleri, Face ID,
  belgeyi telefona kaydetme ve fotoğraf çekme App Store güncellemesi
  olmadan çalışmıyor. Eski sürümde kalan bir kullanıcı bunların hiçbirini
  göremiyor ve bir güncelleme olduğunu bilmiyor.

  KAPATILAMAZ: "Şimdi değil" düğmesi bilerek yok. Kart kapatılamıyor ve
  kapatıldığına dair bir kayıt tutulmuyor; kullanıcı güncellemeden
  uygulamayı her açtığında yeniden karşısına çıkar. Tek çıkış yolu
  güncellemek.

  KİME ÇIKAR: yalnızca uygulamada, yalnızca oturum açmış kullanıcıya ve
  yalnızca sürümü GEREKEN_SURUM'dan eskiyse. Tarayıcıda hiç çıkmaz.

  SÜRÜM OKUNAMAZSA: kart hiç gösterilmez. Güncel bir kullanıcıyı
  yanlışlıkla uygulamadan kilitlemektense hiç göstermemek yeğdir.
*/

/** Bu sürümden eski uygulamalarda uyarı gösterilir. */
const GEREKEN_SURUM = '1.3';

/** "1.10" > "1.9" doğru sonuçlansın diye parça parça sayısal karşılaştırma. */
const surumKucukMu = (a, b) => {
  const pa = String(a || '0').split('.').map((x) => parseInt(x, 10) || 0);
  const pb = String(b || '0').split('.').map((x) => parseInt(x, 10) || 0);
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x !== y) return x < y;
  }
  return false;
};

const yerli = () =>
  typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform();

const oturumVar = () => {
  try {
    return !!(localStorage.getItem('token') || localStorage.getItem('muvekkilToken'));
  } catch (e) {
    return false;
  }
};

const muvekkilMi = () => {
  try {
    return !!localStorage.getItem('muvekkilToken');
  } catch (e) {
    return false;
  }
};

const GuncellemeUyarisi = () => {
  const [acik, setAcik] = useState(false);
  const [muvekkil, setMuvekkil] = useState(false);

  /*
    NE ZAMAN BAKILIYOR: sayfa her degistiginde (Face ID teklifindeki
    hatanin aynisi burada da vardi). Yalnizca uygulama acildiginda
    bakilirsa, o an giris ekraninda olan kullaniciya -- oturum henuz
    yok -- uyari hic cikmiyor, giris yaptiktan sonra da bir daha
    bakilmiyordu.

    MUVEKKILE DE GOSTERILIYOR (kullanici 2026-09-30'da geri istedi).
    1.3 oncesi surumde telefon bildirim adresini alamiyor; muvekkil
    guncellemezse evrak ve durusma bildirimleri ona hic ulasmaz.
  */
  const konum = useLocation();

  useEffect(() => {
    if (acik) return undefined;
    let iptal = false;

    (async () => {
      if (!yerli() || !oturumVar()) return;

      try {
        const { App } = await import('@capacitor/app');
        const bilgi = await App.getInfo();
        if (iptal) return;
        if (surumKucukMu(bilgi?.version, GEREKEN_SURUM)) {
          setMuvekkil(muvekkilMi());
          setAcik(true);
        }
      } catch (e) {
        // Sürüm okunamadı: kart gösterilmez (yukarıdaki açıklamaya bakın).
      }
    })();

    return () => { iptal = true; };
  }, [konum.pathname, acik]);

  const magazayiAc = async () => {
    // Kart KAPATILMIYOR: kullanıcı App Store'a gidip dönerse ve hâlâ eski
    // sürümdeyse aynı ekranla karşılaşsın.
    try {
      const { AppLauncher } = await import('@capacitor/app-launcher');
      // itms-apps şeması App Store uygulamasını DOĞRUDAN açar; https
      // adresi önce Safari'ye uğruyor.
      const sema = 'itms-apps://apps.apple.com/app/id6797491601';
      const { value } = await AppLauncher.canOpenUrl({ url: 'itms-apps://' });
      await AppLauncher.openUrl({ url: value ? sema : APP_STORE_ADRESI });
      return;
    } catch (e) { /* eklenti yoksa aşağıya düşer */ }
    try { window.open(APP_STORE_ADRESI, '_blank', 'noopener,noreferrer'); } catch (e) { /* yoksay */ }
  };

  if (!acik) return null;

  return (
    <div className="gu-katman" role="dialog" aria-modal="true" aria-labelledby="gu-baslik">
      <div className="gu-kutu">
        <div className="gu-simge" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
               strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12" />
            <path d="m7 10 5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
        </div>

        <h2 id="gu-baslik">Uygulamanın yeni sürümü hazır</h2>

        <p>
          {muvekkil
            ? 'Dosyanızla ilgili gelişmelerden anında haberdar olabilmeniz için lütfen uygulamayı güncelleyin.'
            : 'Güncelledikten sonra telefon bildirimleri çalışır; bugünkü duruşmalarınızı ve günün iş listesini ana ekrandan görebilirsiniz.'}
        </p>

        <p className="gu-not">Devam edebilmek için güncellemeniz gerekiyor.</p>

        <button type="button" className="gu-birincil" onClick={magazayiAc}>
          App Store'da Güncelle
        </button>
      </div>
    </div>
  );
};

export default GuncellemeUyarisi;
